// flows/jitReorder.js — Flow 1 „JIT reorder" (Š5): automatický strážca zásob.
// Keď položka klesne pod minimum, appka NAVRHNE objednávku dodávateľovi —
// DRAFT purchase_order (reorder_qty × posledná cena z pohybov). User potvrdí
// kartou (JitReorderCard), NIKDY auto-objednávka: peniaze/mail = vždy draft
// (risk gate). Odoslanie (gmailSend/approve-page/Calendar) príde v surface
// balíku — tu vzniká LEN draft.
//
// needsReorder/buildReorderDraft sú ČISTÉ funkcie (records injektnuté, žiadna
// DB, žiadny zápis) — vzor prijemkaFromDoc. Jediný write helper je
// createPurchaseOrder (vzor createStockItem zo sklad.js — FROZEN, needituje
// sa: purchase_order helper preto žije tu). 0 fetch / 0 LLM / 0 nových
// závislostí — forever-free, local-first.
import { db, initV2, newCommonFields } from '../db';
import { todayIso } from '../dateUtils';
import { stockLevel } from '../modules/sklad';

// „Otvorená" objednávka = draft (čaká na usera) alebo sent (na ceste).
// Closed enum — ďalšie statusy (received/cancelled) objednávku UZATVÁRAJÚ,
// takže reorder smie znova firenúť.
export const OPEN_PO_STATUSES = ['draft', 'sent'];

// Celé nezáporné číslo v centoch (peniaze = int centy, nikdy float — sklad.js).
const isCents = (v) => Number.isInteger(v) && v >= 0;
// Konečné kladné číslo (qty smie byť desatinné, napr. 2.5 kg).
const isPositiveNumber = (v) => typeof v === 'number' && Number.isFinite(v) && v > 0;

// Živá objednávka — cudzie/poškodené records zo syncu sa ticho preskočia.
const isLivePurchaseOrder = (r) =>
  r != null && r.module === 'purchase_order' && r.deleted_at == null;

// Otvorená objednávka = živá + draft/sent. JEDINÁ definícia „otvorenej" PO —
// hasOpenPurchaseOrder aj listOpenPurchaseOrders stavajú na tomto helperi
// (žiadna duplicita filtra, jedna pravda).
const isOpenPurchaseOrder = (r) =>
  isLivePurchaseOrder(r) && OPEN_PO_STATUSES.includes(r.status);

// ─── hasOpenPurchaseOrder — existuje otvorená PO na položku? (čistá fn) ───────
// Blokuje duplicitnú objednávku: kým draft/sent PO na item žije, JIT nefiruje.
export function hasOpenPurchaseOrder(records, itemRef) {
  const recs = Array.isArray(records) ? records : [];
  return recs.some((r) => isOpenPurchaseOrder(r) && r.custom?.item_ref === itemRef);
}

// ─── needsReorder — má položka dostať návrh objednávky? (čistá fn) ───────────
// true ⇔ živý stock_item s deklarovaným min_qty, ktorého balance (reduce
// pohybov, sklad.js stockLevel) je STRIKTNE pod minimom A neexistuje otvorená
// PO na tento item. Karta bez min_qty prah nedeklarovala → nikdy nefiruje
// (žiadny falošný alarm — konzistentné s itemsBelowMin).
export function needsReorder(records, item) {
  if (item == null || item.module !== 'stock_item' || item.deleted_at != null) return false;
  const min = item.custom?.min_qty;
  if (typeof min !== 'number' || !Number.isFinite(min) || min < 0) return false;
  const itemRef = String(item.id);
  if (stockLevel(records, itemRef) >= min) return false;
  return !hasOpenPurchaseOrder(records, itemRef);
}

// ─── lastUnitCostCents — posledná známa NÁKUPNÁ cena položky (čistá fn) ──────
// „Posledný" = najvyšší (entry_date, created_at) medzi živými PRÍJMOVÝMI
// pohybmi itemu, ktoré cenu MAJÚ (valídne int centy). MONEY GATE: len
// direction='in' — cena na výdaji (out) je predajná/odpisová, NIE nákupná,
// a do objednávky dodávateľovi nesmie presiaknuť. Pohyb bez ceny sa preskočí —
// keď cenu nepoznáme, vraciame null a suma sa NIKDY nefabrikuje (honest numbers).
export function lastUnitCostCents(records, itemRef) {
  const recs = Array.isArray(records) ? records : [];
  let best = null;
  for (const r of recs) {
    if (r == null || r.module !== 'stock_move' || r.deleted_at != null) continue;
    if (r.direction !== 'in') continue; // nákupná cena LEN z príjmov
    if (r.custom?.item_ref !== itemRef) continue;
    if (!isCents(r.unit_cost_cents)) continue;
    if (
      best == null ||
      (r.entry_date || '') > (best.entry_date || '') ||
      ((r.entry_date || '') === (best.entry_date || '') &&
        (r.created_at || '') > (best.created_at || ''))
    ) {
      best = r;
    }
  }
  return best ? best.unit_cost_cents : null;
}

// ─── buildReorderDraft — DRAFT objednávky (NIČ nezapisuje) ───────────────────
// qty = item.custom.reorder_qty (bez neho sa draft NEDÁ postaviť → null,
// nefabrikuj množstvo), cena = posledný pohyb s cenou, amount = qty × cena
// zaokrúhlené RAZ (qty smie byť desatinné, vzor stockValueCents). Bez ceny →
// unit_cost_cents/amount_cents null — draft bez sumy je čestnejší než odhad.
export function buildReorderDraft(item, records) {
  if (item == null || item.module !== 'stock_item' || item.deleted_at != null) return null;
  const qty = item.custom?.reorder_qty;
  if (!isPositiveNumber(qty)) return null;

  const itemRef = String(item.id);
  const unitCostCents = lastUnitCostCents(records, itemRef);

  return {
    item_ref: itemRef,
    label: item.label || null,
    qty,
    unit: item.unit || 'ks',
    unit_cost_cents: unitCostCents,
    supplier_ref: item.custom?.supplier_ref || null,
    amount_cents: unitCostCents == null ? null : Math.round(qty * unitCostCents),
  };
}

// ─── createPurchaseOrder — JEDINÝ write helper flowu (module='purchase_order')
// input = { item_ref (povinné), qty (> 0 povinné), unit?, unit_cost_cents?
//           (int centy|null), supplier_ref?, amount_cents? (int centy|null),
//           label?, source? }
// status VŽDY 'draft' — odoslanie (draft→sent) je surface balík; poCommittedCents
// aj metrika na_ceste rátajú len 'sent', takže draft neinflatuje cash_out.
// qty/unit/unit_cost_cents → rezervované stĺpce kernelu (vzor createStockMove);
// item_ref/supplier_ref → custom JSON (kontrakt Š5 — top-level NOVÉ pole by
// zhodilo INSERT). Bez ceny → amount_cents null, NIKDY 0-hack. Chyby hádže
// (write-side gate). RACE GATE: tesne pred zápisom sa ZNOVA overí otvorená PO —
// needsReorder beží pri renderi a dva taby/zariadenia (offline-first sync) by
// inak obe zapísali duplicitný draft (useRef chráni len jednu inštanciu karty).
export async function createPurchaseOrder(input) {
  if (typeof input?.item_ref !== 'string' || input.item_ref.trim() === '') {
    throw new Error('item_ref je povinný');
  }
  if (!isPositiveNumber(input.qty)) {
    throw new Error('qty musí byť číslo > 0');
  }
  if (input.unit_cost_cents != null && !isCents(input.unit_cost_cents)) {
    throw new Error('unit_cost_cents musí byť celé nezáporné číslo v centoch');
  }
  if (input.amount_cents != null && !isCents(input.amount_cents)) {
    throw new Error('amount_cents musí byť celé nezáporné číslo v centoch');
  }
  // Interná konzistencia: keď prišla cena AJ suma, suma MUSÍ byť qty × cena
  // zaokrúhlené RAZ (buildReorderDraft to tak ráta — helper to vynúti, aby
  // ručný caller nezapísal rozchádzajúce sa peniaze).
  if (
    input.unit_cost_cents != null &&
    input.amount_cents != null &&
    input.amount_cents !== Math.round(input.qty * input.unit_cost_cents)
  ) {
    throw new Error('amount_cents nesedí s qty × unit_cost_cents');
  }

  // RACE GATE — re-check tesne pred zápisom (poistka proti multi-tab/device
  // duplikátu): render-time needsReorder mohol byť stale.
  await initV2();
  const existing = await db.records.where('module').equals('purchase_order').toArray();
  if (hasOpenPurchaseOrder(existing, input.item_ref)) {
    throw new Error('Otvorená objednávka pre túto položku už existuje');
  }

  const record = {
    ...newCommonFields(),
    module: 'purchase_order',
    entry_date: todayIso(),
    label: (typeof input.label === 'string' && input.label.trim()) || null,
    qty: input.qty, // rezervované stĺpce kernelu ↓ (vzor createStockMove)
    unit: input.unit || 'ks',
    unit_cost_cents: input.unit_cost_cents ?? null,
    amount_cents: input.amount_cents ?? null,
    status: 'draft', // RISK GATE: nikdy 'sent' pri vzniku — odoslanie je user akcia
    source: input.source || 'jit-reorder',
    // item_ref/supplier_ref — JEDINÉ miesto je custom JSON (kontrakt Š5)
    custom: {
      item_ref: input.item_ref,
      supplier_ref: input.supplier_ref || null,
    },
  };

  await db.records.add(record);
  return record;
}

// ─── listOpenPurchaseOrders — DB ČÍTANIE (žiadny zápis, vzor listStockItems) ─
// Otvorené (draft/sent, nezmazané) objednávky; itemRef zúži na jednu položku.
export async function listOpenPurchaseOrders(itemRef) {
  await initV2();
  const rows = await db.records
    .where('module')
    .equals('purchase_order')
    .filter(isOpenPurchaseOrder)
    .toArray();
  return itemRef == null ? rows : rows.filter((r) => r.custom?.item_ref === itemRef);
}
